import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export interface ProviderKey {
  id: string;
  provider: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  lastUsed: string | null;
  isGlobal: boolean;
  additionalConfig?: Record<string, any>;
  verificationStatus?: 'verified' | 'failed' | 'unknown';
  modelConfig?: {
    defaultModel?: string;
    allowedModels?: string[];
    modelSettings?: Record<string, any>;
  };
}

export interface Provider {
  id: string;
  name: string;
  description: string;
  requiresEndpoint?: boolean;
}

interface ProviderKeyContextType {
  providerKeys: ProviderKey[];
  providers: Provider[];
  isLoading: boolean;
  createProviderKey: (data: { 
    provider: string;
    name: string;
    apiKey: string;
    additionalConfig?: any;
    modelConfig?: {
      defaultModel?: string;
      allowedModels?: string[];
      modelSettings?: Record<string, any>;
    };
    forceCreate?: boolean;
  }) => Promise<ProviderKey>;
  updateProviderKey: (id: string, data: { 
    name?: string;
    apiKey?: string;
    isActive?: boolean;
    additionalConfig?: any;
    modelConfig?: {
      defaultModel?: string;
      allowedModels?: string[];
      modelSettings?: Record<string, any>;
    };
  }) => Promise<ProviderKey>;
  deleteProviderKey: (id: string) => Promise<void>;
  refreshProviderKeys: () => Promise<void>;
  verifyProviderKey: (provider: string, apiKey: string, additionalConfig?: any, id?: string) => Promise<{isValid: boolean; message: string}>;
  verifyProviderKeyById: (id: string) => Promise<{isValid: boolean; message: string}>;
  findExistingProviderKey: (provider: string, name: string) => ProviderKey | undefined;
}

const ProviderKeyContext = createContext<ProviderKeyContextType | undefined>(undefined);

export function ProviderKeyProvider({ children }: { children: ReactNode }) {
  const [providerKeys, setProviderKeys] = useState<ProviderKey[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get authentication status
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Listen for updates so we can refresh the provider keys list
  const [shouldRefresh, setShouldRefresh] = useState(false);
  
  const fetchProviders = async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await api.get('/provider-keys/providers');
      if (response && response.success && response.providers) {
        setProviders(response.providers);
      }
    } catch (error) {
      console.error('Failed to load providers:', error);
      toast.error('Failed to load provider list');
    }
  };

  const fetchProviderKeys = async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      logger.log("Fetching provider keys from server");
      const response = await api.get('/provider-keys');
      logger.log("Provider keys response:", response);
      
      if (response && response.success && response.providerKeys) {
        // Process keys to ensure modelConfig is properly structured
        const processedKeys = response.providerKeys.map((key: any) => {
          // Ensure modelConfig is properly structured
          let modelConfig = key.modelConfig;
          
          // If modelConfig is missing or empty, check if it's in additionalConfig
          if ((!modelConfig || Object.keys(modelConfig).length === 0) && 
              key.additionalConfig && key.additionalConfig.modelConfig) {
            modelConfig = key.additionalConfig.modelConfig;
          }
          
          // Ensure modelConfig has the expected structure
          if (!modelConfig || typeof modelConfig !== 'object') {
            modelConfig = { defaultModel: '', allowedModels: [] };
          }
          
          if (!modelConfig.allowedModels) {
            modelConfig.allowedModels = [];
          }
          
          if (!modelConfig.defaultModel) {
            modelConfig.defaultModel = modelConfig.allowedModels.length > 0 
              ? modelConfig.allowedModels[0]
              : '';
          }
          
          return {
            ...key,
            modelConfig
          };
        });
        
        logger.log(`Loaded ${processedKeys.length} provider keys`);
        setProviderKeys(processedKeys);
      } else {
        console.error("Invalid response format or empty provider keys:", response);
        // If the response is empty but was successful, set empty array to prevent loading state
        if (response && response.success) {
          setProviderKeys([]);
        }
      }
    } catch (error) {
      console.error('Failed to load provider API keys:', error);
      toast.error('Failed to load provider API keys. Try refreshing the page.', {
        action: {
          label: "Retry",
          onClick: () => refreshProviderKeys()
        },
        duration: 5000
      });
      // Set empty array to clear loading state
      setProviderKeys([]);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshProviderKeys = async () => {
    logger.log("Manually refreshing provider keys");
    setIsLoading(true);
    try {
      await fetchProviderKeys();
      toast.success('Provider API keys refreshed');
    } catch (error) {
      console.error("Error refreshing provider keys:", error);
      toast.error('Failed to refresh provider keys');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch data when authentication is resolved and user is authenticated
    if (!authLoading && isAuthenticated) {
      fetchProviders();
      fetchProviderKeys();
    } else if (!authLoading && !isAuthenticated) {
      // Not authenticated, set loading to false
      setIsLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    if (shouldRefresh) {
      fetchProviderKeys();
      setShouldRefresh(false);
    }
  }, [shouldRefresh]);

  const findExistingProviderKey = (provider: string, name: string): ProviderKey | undefined => {
    return providerKeys.find(key => 
      key.provider === provider && 
      key.name === name
    );
  };

  const createProviderKey = async (data: { 
    provider: string;
    name: string;
    apiKey: string;
    additionalConfig?: any;
    modelConfig?: {
      defaultModel?: string;
      allowedModels?: string[];
      modelSettings?: Record<string, any>;
    };
    forceCreate?: boolean;
  }): Promise<ProviderKey> => {
    try {
      // First check if a key with this name already exists for this provider
      logger.log(`Checking if key with name "${data.name}" for provider "${data.provider}" already exists`);
      const existingKeys = providerKeys.filter(key => 
        key.provider === data.provider && key.name === data.name
      );
      
      if (existingKeys.length > 0 && !data.forceCreate) {
        logger.log(`Found ${existingKeys.length} existing keys with the same name and provider:`, existingKeys);
        const existingKey = existingKeys[0];
        
        // Create a custom error with additional data for the calling component to handle
        const customError = new Error('A key with this name already exists for this provider');
        // @ts-ignore - Add extra properties to the error
        customError.isDuplicateKey = true;
        // @ts-ignore
        customError.existingKey = existingKey;
        throw customError;
      }
      
      // Log the provider keys we checked against
      logger.log(`Current provider keys (${providerKeys.length}):`, 
        providerKeys.map(k => ({id: k.id, name: k.name, provider: k.provider}))
      );
      
      // Include forceCreate flag for problematic keys
      const requestData = {
        ...data,
        forceCreate: data.forceCreate || existingKeys.length > 0
      };
      
      // No duplicate found or force create enabled, proceed with creation
      logger.log("No duplicate found or force creating, proceeding with key creation");
      
      try {
        const response = await api.post('/provider-keys', requestData);
        
        if (response && response.success) {
          logger.log("Provider key created successfully:", response);
          
          // Ensure response key is properly processed
          const newKey = response.providerKey || response.key;
          
          if (!newKey) {
            console.error("Invalid response format for created provider key:", response);
            throw new Error("Server returned invalid response format");
          }
          
          // Ensure modelConfig is properly structured in the new key
          let modelConfig = newKey.modelConfig;
          
          // If modelConfig is missing or empty, check if it's in additionalConfig
          if ((!modelConfig || Object.keys(modelConfig).length === 0) && 
              newKey.additionalConfig && newKey.additionalConfig.modelConfig) {
            modelConfig = newKey.additionalConfig.modelConfig;
          }
          
          // Format consistent with our expected structure
          const formattedKey = {
            ...newKey,
            modelConfig: modelConfig || { defaultModel: '', allowedModels: [] },
          };
          
          // Update our local state with the new key
          setProviderKeys(prev => [...prev, formattedKey]);
          
          // Trigger a refresh to ensure consistency
          setShouldRefresh(true);
          
          toast.success(`Created provider key "${data.name}" successfully`);
          
          return formattedKey;
        } else {
          throw new Error(response?.message || 'Failed to create provider API key');
        }
      } catch (apiError) {
        console.error("API error creating provider key:", apiError);
        
        // Special handling for 409 Conflict errors (duplicate key)
        // @ts-ignore
        if (apiError.status === 409 || apiError.isConflict) {
          logger.log("Detected conflict error from server");
          
          // If we want to force create, try again with force flag
          if (data.forceCreate) {
            logger.log("Force create is enabled, trying again with stronger force flag");
            try {
              const forceResponse = await api.post('/provider-keys', { 
                ...data, 
                forceCreate: true,
                _bypassCache: Date.now() // Add cache busting parameter
              });
              
              if (forceResponse && forceResponse.success) {
                logger.log("Provider key force-created successfully:", forceResponse);
                const forcedKey = forceResponse.providerKey || forceResponse.key;
                
                // Format the key and update state
                const formattedKey = {
                  ...forcedKey,
                  modelConfig: forcedKey.modelConfig || { defaultModel: '', allowedModels: [] },
                };
                
                setProviderKeys(prev => [...prev, formattedKey]);
                setShouldRefresh(true);
                toast.success(`Created provider key "${data.name}" successfully`);
                return formattedKey;
              }
            } catch (forceError) {
              console.error("Even force creation failed:", forceError);
              throw forceError;
            }
          }
          
          // Refresh the keys list to make sure we have the latest
          await fetchProviderKeys();
          
          // Try to find the existing key again
          const refreshedExistingKey = providerKeys.find(key => 
            key.provider === data.provider && key.name === data.name
          );
          
          if (refreshedExistingKey) {
            logger.log("Found existing key after refresh:", refreshedExistingKey);
            
            // Create a custom error with the existing key data
            const conflictError = new Error('A key with this name already exists for this provider');
            // @ts-ignore
            conflictError.isDuplicateKey = true;
            // @ts-ignore
            conflictError.existingKey = refreshedExistingKey;
            // @ts-ignore
            conflictError.status = 409;
            
            throw conflictError;
          }
        }
        
        // If it's not a specific error we can handle, rethrow
        throw apiError;
      }
    } catch (error) {
      console.error("Failed to create provider API key:", error);
      throw error;
    }
  };

  const updateProviderKey = async (id: string, data: { 
    name?: string;
    apiKey?: string;
    isActive?: boolean;
    additionalConfig?: any;
    modelConfig?: {
      defaultModel?: string;
      allowedModels?: string[];
      modelSettings?: Record<string, any>;
    };
  }): Promise<ProviderKey> => {
    try {
      const response = await api.put(`/provider-keys/${id}`, data);
      
      if (response && response.success) {
        // Check for either response.key or response.providerKey since backend returns key
        const updatedKey = response.key || response.providerKey;
        if (updatedKey) {
          setProviderKeys(prevKeys => 
            prevKeys.map(key => key.id === id ? updatedKey : key)
          );
          toast.success('Provider API key updated successfully');
          
          // Set flag to refresh the provider keys list
          setShouldRefresh(true);
          
          return updatedKey;
        }
        
        // If the response was successful but didn't include a key object,
        // we still want to refresh the list
        await fetchProviderKeys();
        toast.success('Provider API key updated successfully');
        
        // Return a minimal object to satisfy the Promise return type
        return {
          id, 
          provider: '',
          name: data.name || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isActive: data.isActive !== undefined ? data.isActive : true,
          lastUsed: null,
          isGlobal: false
        };
      } else {
        throw new Error(response.message || 'Failed to update provider API key');
      }
    } catch (error) {
      console.error('Failed to update provider API key:', error);
      
      // Improved error handling with specific error message
      let errorMessage = 'Failed to update provider API key';
      
      if (error.message) {
        errorMessage = `Failed to update provider API key: ${error.message}`;
      }
      
      if (error.response && error.response.status) {
        // Add status code to error message if available
        errorMessage += ` (Status: ${error.response.status})`;
      }
      
      toast.error(errorMessage);
      throw error;
    }
  };

  const deleteProviderKey = async (id: string) => {
    try {
      const response = await api.delete(`/provider-keys/${id}`);
      
      if (response && response.success) {
        setProviderKeys(prevKeys => prevKeys.filter(key => key.id !== id));
        toast.success('Provider API key deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete provider API key');
      }
    } catch (error) {
      console.error('Failed to delete provider API key:', error);
      toast.error('Failed to delete provider API key');
      throw error;
    }
  };

  const verifyProviderKey = async (provider: string, apiKey: string, additionalConfig?: any, id?: string) => {
    try {
      const response = await api.post('/provider-keys/verify', { 
        provider,
        apiKey,
        additionalConfig,
        id
      });
      
      if (response && response.success) {
        // If we successfully verified and have an ID, update the local state
        if (id && response.verificationStatus) {
          setProviderKeys(prevKeys => 
            prevKeys.map(key => 
              key.id === id 
                ? { ...key, verificationStatus: response.verificationStatus } 
                : key
            )
          );
        }
        
        return {
          isValid: response.isValid,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to verify provider API key');
      }
    } catch (error) {
      console.error('Failed to verify provider API key:', error);
      return {
        isValid: false,
        message: error.message || 'Failed to verify provider API key'
      };
    }
  };

  const verifyProviderKeyById = async (id: string) => {
    try {
      const response = await api.post(`/provider-keys/verify-by-id/${id}`, undefined, true);
      
      if (response && response.success) {
        // Update the local state
        if (response.verificationStatus) {
          setProviderKeys(prevKeys => 
            prevKeys.map(key => 
              key.id === id 
                ? { ...key, verificationStatus: response.verificationStatus } 
                : key
            )
          );
        }
        
        return {
          isValid: response.isValid,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to verify provider API key');
      }
    } catch (error) {
      console.error('Failed to verify provider API key by ID:', error);
      return {
        isValid: false,
        message: error.message || 'Failed to verify provider API key'
      };
    }
  };

  return (
    <ProviderKeyContext.Provider value={{ 
      providerKeys, 
      providers,
      isLoading, 
      createProviderKey, 
      updateProviderKey, 
      deleteProviderKey,
      refreshProviderKeys,
      verifyProviderKey,
      verifyProviderKeyById,
      findExistingProviderKey
    }}>
      {children}
    </ProviderKeyContext.Provider>
  );
}

export function useProviderKeys() {
  const context = useContext(ProviderKeyContext);
  if (context === undefined) {
    throw new Error('useProviderKeys must be used within a ProviderKeyProvider');
  }
  return context;
} 